import { useEffect, useRef, useState } from "react";
import { formatTimeOfDay, parseTimeOfDay, stepTimeOfDay } from "../../utils/timesheets";

const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 06 - 21
const QUARTERS = [0, 15, 30, 45];

/**
 * Tijdsveld voor start- en einduur.
 * - Typen: elk geldig tijdstip ("9", "930", "9:35", "14u15").
 * - Pijltjes omhoog/omlaag: 5 minuten; Shift 15 minuten; Ctrl 1 uur.
 * - Muis: kies een uur, daarna een kwartier.
 * value is "HH:MM" of "".
 */
export default function TimeInput({ value, onChange, label, id, autoFocus }) {
  const [text, setText] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [pickedHour, setPickedHour] = useState(null);
  const wrapper = useRef(null);
  const emitted = useRef(value || "");

  // Een waarde die van buitenaf komt (bv. bewerken) overschrijft de tekst; onze eigen uitgestuurde waarde niet,
  // anders wordt tijdens het typen "9" meteen "09:00" en loopt de invoer in de soep.
  useEffect(() => {
    if ((value || "") !== emitted.current) {
      emitted.current = value || "";
      setText(value || "");
    }
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapper.current && !wrapper.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const minutes = parseTimeOfDay(text);
  const invalid = text.trim() !== "" && minutes === null;

  const emit = (formatted) => {
    emitted.current = formatted;
    onChange(formatted);
  };

  const commit = (nextMinutes) => {
    const formatted = formatTimeOfDay(nextMinutes);
    setText(formatted);
    emit(formatted);
  };

  const handleChange = (e) => {
    const next = e.target.value;
    setText(next);
    const parsed = parseTimeOfDay(next);
    if (parsed !== null) emit(formatTimeOfDay(parsed));
    else if (next.trim() === "") emit("");
  };

  const handleBlur = () => {
    if (minutes !== null) setText(formatTimeOfDay(minutes));
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      commit(stepTimeOfDay(minutes, e.key === "ArrowUp" ? 1 : -1, { shift: e.shiftKey, ctrl: e.ctrlKey }));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const pickQuarter = (hour, quarter) => {
    commit(hour * 60 + quarter);
    setOpen(false);
    setPickedHour(null);
  };

  const currentHour = minutes !== null ? Math.floor(minutes / 60) : null;
  const hourShown = pickedHour !== null ? pickedHour : currentHour;

  return (
    <div
      className="relative"
      ref={wrapper}
      onBlur={(e) => {
        // focus verlaat het hele veld (bv. met Tab): kiezer sluiten
        if (!wrapper.current.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      {label && (
        <label htmlFor={id} className="block text-xs font-medium text-gray-600 mb-1">
          {label}
        </label>
      )}
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        autoFocus={autoFocus}
        value={text}
        placeholder="uu:mm"
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onFocus={() => setOpen(true)}
        aria-invalid={invalid}
        className={`w-full border rounded px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 ${
          invalid ? "border-red-400 focus:ring-red-400" : "border-gray-300 focus:ring-blue-500"
        }`}
      />
      {open && (
        <div className="absolute z-30 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-2">
          <div className="grid grid-cols-6 gap-1">
            {HOURS.map((h) => (
              <button
                key={h}
                type="button"
                tabIndex={-1}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setPickedHour(h)}
                className={`py-1 text-xs rounded border ${
                  hourShown === h ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {String(h).padStart(2, "0")}
              </button>
            ))}
          </div>
          {hourShown !== null && (
            <div className="grid grid-cols-4 gap-1 mt-2 pt-2 border-t border-gray-100">
              {QUARTERS.map((q) => (
                <button
                  key={q}
                  type="button"
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickQuarter(hourShown, q)}
                  className={`py-1 text-xs rounded border tabular-nums ${
                    minutes === hourShown * 60 + q
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {String(hourShown).padStart(2, "0")}:{String(q).padStart(2, "0")}
                </button>
              ))}
            </div>
          )}
          <p className="mt-2 text-[11px] text-gray-400">
            Typ een tijdstip, of gebruik ↑ ↓ (5 min · Shift 15 min · Ctrl 1 uur).
          </p>
        </div>
      )}
    </div>
  );
}
